import React, { useEffect } from 'react'
import { useState } from 'react'
import {
  AssetTagsResponse,
  Campaign,
  editMultipleCampaigns,
  getAssetTagsQuery_v2,
  isCampaignEditable,
} from '../../../../Api/campaigns'
import { LargeDialog } from '../../../../Components/LargeDialog'
import {
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Stack,
} from 'office-ui-fabric-react'
import { cloneDeep } from 'lodash'
import { templateTitleToRenderedTitle } from '../../../../Functions/templateTitle'
import { ChildProps, graphql } from 'react-apollo'
import { TitleInput } from './TitleInput'
import { TargetedAssetsInput } from './TargetedAssetsInput'
import { PriorityInput } from './PriorityInput'
import { SamePriorityBehaviorInput } from './SamePriorityBehaviorInput'
import { BidAdapterAliasInput } from './BidAdapterAliasInput'
import { not } from '../../../../Utils/Fp'
import { PriceFloorInput } from './PriceFloorInput'
import { Currencies } from '../../../../Constants/Currencies'
import { ThirdPartySellerDomainInput } from './ThirdPartySellerDomainInput'
import { useInputFieldOptions } from './useInputFieldOptions'
import { ConfirmationDialog } from './ConfirmationDialog'
import { removeNullFields } from '../../../../Utils/object'
import { ErrorDialog } from '../../../../Components/ErrorDialog'

interface Props {
  campaignsToEdit: Campaign[]
  onClose: () => void
  onUpdated: () => void
}

const withCreateGql = graphql<Props, AssetTagsResponse>(getAssetTagsQuery_v2, {
  options: () => ({ notifyOnNetworkStatusChange: true }),
})
export const MultiEditCampaignDialog = withCreateGql(
  MultiEditCampaignDialogView
)

function MultiEditCampaignDialogView({
  data,
  campaignsToEdit,
  onClose,
  onUpdated,
}: ChildProps<Props, AssetTagsResponse>) {
  const editableCampaigns = campaignsToEdit.filter(isCampaignEditable)
  const [genericError, setGenericError] = useState<boolean>(false)
  const [campaignsToSave, setCampaignsToSave] = useState<Campaign[]>([])
  const [inputField, setInputField] = useState<string | undefined>(undefined)
  const [title, setTitle] = useState<string>('')
  const [targetedAssets, setTargetedAssets] = useState<string[]>([])
  const [priority, setPriority] = useState<number>(5)
  const [fixedPrebidValue, setFixedPrebidValue] = useState<number | null>(null)
  const [fixedPrebidCurrency, setFixedPrebidCurrency] = useState<string | null>(
    null
  )
  const [bidAdapterAlias, setBidAdapterAlias] = useState<string>('')
  const [priceFloor, setPriceFloor] = useState<number>(0)
  const [priceFloorCurrency, setPriceFloorCurrency] = useState<Currencies>(
    Currencies.EUR
  )
  const [thirdPartyReferenceChainId, setThirdPartyReferenceChainId] = useState<
    string | null
  >(null)
  const [inputErrorMessage, setInputErrorMessage] = useState<string>('')
  const [isSubmitting, setSubmitting] = useState(false)
  const [confirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false)
  const showConfirmationDialog = () => setShowConfirmationDialog(true)
  const hideConfirmationDialog = () => setShowConfirmationDialog(false)
  const inputFieldOptions = useInputFieldOptions()
  const isHbInputField = [
    'headerBiddingAdUnitBidAdapterAlias',
    'headerBiddingAdUnitMinimumPriceFloor',
  ].includes(inputField || '')
  const onSave = () => {
    const campaigns = isHbInputField
      ? editableCampaigns.filter(isHbCampaign)
      : editableCampaigns
    const clonedCampaigns = campaigns.map(cloneDeep)
    const error = getError({
      inputField,
      title,
      targetedAssets,
      priority,
      fixedPrebidValue,
      fixedPrebidCurrency,
      bidAdapterAlias,
      priceFloor,
      priceFloorCurrency,
      campaigns: clonedCampaigns,
      assetTags: data! as AssetTagsResponse,
    })
    if (error) {
      setInputErrorMessage(error)
      return
    }
    setInputErrorMessage('')
    const updatedCampaigns = getUpdatedCampaigns({
      campaigns: clonedCampaigns,
      inputField: inputField || '',
      title,
      targetedAssets,
      priority,
      fixedPrebidValue,
      fixedPrebidCurrency,
      bidAdapterAlias,
      priceFloor,
      priceFloorCurrency,
      thirdPartyReferenceChainId,
      assetTags: data! as AssetTagsResponse,
    })
    const sanitizedCampaigns = sanitizeCampaigns(updatedCampaigns)
    setCampaignsToSave(sanitizedCampaigns)
    showConfirmationDialog()
  }
  const onConfirm = async () => {
    setSubmitting(true)
    try {
      await editMultipleCampaigns(campaignsToSave)
    } catch (error) {
      console.error(error)
      setGenericError(true)
      setSubmitting(false)
      return
    }
    setSubmitting(false)
    hideConfirmationDialog()
    onUpdated()
  }
  useEffect(() => {
    if (!!bidAdapterAlias) {
      return
    }
    const firstHbCampaign = editableCampaigns.find(isHbCampaign)
    if (!firstHbCampaign) {
      return
    }
    const bidderParams = JSON.parse(
      firstHbCampaign.asset!.headerBiddingSource!.bidderParamsJson
    )
    setBidAdapterAlias(bidderParams.bidder)
  }, [setBidAdapterAlias, editableCampaigns, bidAdapterAlias])
  useEffect(() => {
    setInputErrorMessage('')
  }, [setInputErrorMessage, inputField])
  const nonHbCampaigns = editableCampaigns.filter(not(isHbCampaign))
  const changeCanBeApplied =
    editableCampaigns.length > 0 &&
    (!isHbInputField || editableCampaigns.some(isHbCampaign))
  const notEditableCampaigns = campaignsToEdit.filter(not(isCampaignEditable))
  return (
    <>
      {genericError ? (
        <ErrorDialog
          onClose={() => setGenericError(false)}
          title="Error"
          message="Could not edit the campaigns."
        />
      ) : null}
      <LargeDialog
        isBlocking={true}
        title="Edit multiple campaigns"
        onClose={onClose}
        width={600}
        buttons={[
          {
            name: 'Cancel',
            action: onClose,
            disabled: isSubmitting,
            primary: false,
          },
          {
            name: 'Save',
            action: onSave,
            disabled: isSubmitting || !inputField || !changeCanBeApplied,
            primary: true,
          },
        ]}
      >
        <Stack gap={16}>
          <PreventInputFieldDefaultSelection />
          <Dropdown
            label="Input field"
            selectedKey={inputField}
            placeholder="Please select an input field"
            options={inputFieldOptions}
            onChange={(
              _event: React.FormEvent<HTMLDivElement>,
              item: IDropdownOption | undefined
            ): void => {
              setInputField(item ? (item.key as string) : undefined)
            }}
          />
          {inputField === 'title' ? (
            <TitleInput
              title={title}
              setTitle={setTitle}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
          {inputField === 'priority' ? (
            <PriorityInput
              priority={priority}
              setPriority={setPriority}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
          {inputField === 'targetedAssets' ? (
            <TargetedAssetsInput
              targetedAssets={targetedAssets}
              setTargetedAssets={setTargetedAssets}
              assetTagsResponse={data! as AssetTagsResponse}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
          {inputField === 'samePriorityBehavior' ? (
            <SamePriorityBehaviorInput
              fixedPrebidCurrency={fixedPrebidCurrency}
              fixedPrebidValue={fixedPrebidValue}
              setFixedPrebidCurrency={setFixedPrebidCurrency}
              setFixedPrebidValue={setFixedPrebidValue}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
          {inputField === 'headerBiddingAdUnitBidAdapterAlias' ? (
            <BidAdapterAliasInput
              bidAdapterAlias={bidAdapterAlias}
              setBidAdapterAlias={setBidAdapterAlias}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
          {inputField === 'headerBiddingAdUnitMinimumPriceFloor' ? (
            <PriceFloorInput
              currency={priceFloorCurrency}
              setCurrency={setPriceFloorCurrency}
              priceFloor={priceFloor}
              setPriceFloor={setPriceFloor}
              disabled={isSubmitting}
              errorMessage={inputErrorMessage}
            />
          ) : null}
          {inputField === 'thirdPartySellerDomain' ? (
            <ThirdPartySellerDomainInput
              thirdPartyReferenceChainId={thirdPartyReferenceChainId}
              setThirdPartyReferenceChainId={setThirdPartyReferenceChainId}
              errorMessage={inputErrorMessage}
              disabled={isSubmitting}
            />
          ) : null}
        </Stack>
        {changeCanBeApplied && isHbInputField && nonHbCampaigns.length > 0 ? (
          <MessageBar
            messageBarType={MessageBarType.warning}
            isMultiline={true}
          >
            The following campaigns will be ignored, as the{' '}
            <strong>Ad Source</strong> is not{' '}
            <strong>Header Bidding Ad Unit</strong>
            <ul>
              {nonHbCampaigns.map((campaign) => (
                <li key={campaign.id}>{campaign.title}</li>
              ))}
            </ul>
          </MessageBar>
        ) : null}
        {changeCanBeApplied && notEditableCampaigns.length > 0 ? (
          <MessageBar
            messageBarType={MessageBarType.warning}
            isMultiline={true}
          >
            The following campaigns will be ignored, as they are in{' '}
            <strong>read-only</strong> mode
            <ul>
              {notEditableCampaigns.map((campaign) => (
                <li key={campaign.id}>{campaign.title}</li>
              ))}
            </ul>
          </MessageBar>
        ) : null}
        {!changeCanBeApplied ? (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
            None of the selected campaigns can be modified. Please change the
            Input Field or change the campaign selection.
          </MessageBar>
        ) : null}
      </LargeDialog>
      {confirmationDialog ? (
        <ConfirmationDialog
          originalCampaigns={campaignsToEdit}
          campaignsToSave={campaignsToSave}
          inputField={inputField || ''}
          disabled={isSubmitting}
          onClose={hideConfirmationDialog}
          onConfirm={onConfirm}
        />
      ) : null}
    </>
  )
}

function getError({
  inputField,
  title,
  targetedAssets,
  priority,
  fixedPrebidValue,
  fixedPrebidCurrency,
  bidAdapterAlias,
  priceFloor,
  priceFloorCurrency,
  campaigns,
  assetTags,
}: {
  inputField: string | undefined
  title: string
  targetedAssets: string[]
  priority: number
  fixedPrebidValue: number | null
  fixedPrebidCurrency: string | null
  bidAdapterAlias: string
  priceFloor: number
  priceFloorCurrency: Currencies
  campaigns: Campaign[]
  assetTags: AssetTagsResponse
}): string | undefined {
  switch (inputField) {
    case 'title': {
      const titles = campaigns.map((campaign) =>
        templateTitleToRenderedTitle(title, campaign, assetTags).trim()
      )
      return titles.some((title) => !title)
        ? 'Title would be empty for at least one campaign'
        : ''
    }
    case 'targetedAssets': {
      return targetedAssets.length === 0
        ? 'At least one targeted asset is required'
        : ''
    }
    case 'priority': {
      return priority <= 0 ? 'Priority must be greater than 0' : ''
    }
    case 'samePriorityBehavior': {
      if (fixedPrebidValue != null) {
        if (fixedPrebidValue <= 0) {
          return 'Fixed Prebid Value must be greater than 0'
        }
        if (!fixedPrebidCurrency) {
          return 'Fixed Prebid Currency must be set'
        }
      }
      return ''
    }
    case 'headerBiddingAdUnitBidAdapterAlias': {
      return bidAdapterAlias.trim() === ''
        ? 'Bid Adapter Alias cannot be empty'
        : ''
    }
    case 'headerBiddingAdUnitMinimumPriceFloor': {
      if (!priceFloorCurrency) {
        return 'Price Floor Currency must be set'
      }
      return priceFloor < 0 ? 'Price Floor must be a positive number' : ''
    }
  }
}

function getUpdatedCampaigns({
  campaigns,
  inputField,
  title,
  targetedAssets,
  priority,
  fixedPrebidValue,
  fixedPrebidCurrency,
  bidAdapterAlias,
  priceFloor,
  priceFloorCurrency,
  thirdPartyReferenceChainId,
  assetTags,
}: {
  campaigns: Campaign[]
  inputField: string
  title: string
  targetedAssets: string[]
  priority: number
  fixedPrebidValue: number | null
  fixedPrebidCurrency: string | null
  bidAdapterAlias: string
  priceFloor: number
  priceFloorCurrency: Currencies
  thirdPartyReferenceChainId: string | null
  assetTags: AssetTagsResponse
}) {
  return campaigns.map((campaign) => {
    const updatedCampaign = cloneDeep(campaign)
    switch (inputField) {
      case 'title': {
        updatedCampaign.title = templateTitleToRenderedTitle(
          title,
          campaign,
          assetTags
        ).slice(0, 100)
        break
      }
      case 'targetedAssets': {
        updatedCampaign.targetedAssets = targetedAssets
        break
      }
      case 'priority': {
        updatedCampaign.priority = priority
        break
      }
      case 'samePriorityBehavior': {
        updatedCampaign.fixedPrebidValue = fixedPrebidValue
        updatedCampaign.fixedPrebidCurrency = fixedPrebidCurrency
        break
      }
      case 'headerBiddingAdUnitBidAdapterAlias': {
        if (
          updatedCampaign.asset &&
          updatedCampaign.asset.headerBiddingSource
        ) {
          const bidderParams = JSON.parse(
            updatedCampaign.asset.headerBiddingSource.bidderParamsJson
          )
          bidderParams.alias = bidAdapterAlias
          updatedCampaign.asset.headerBiddingSource.bidderParamsJson =
            JSON.stringify(bidderParams, null, 4)
        }
        break
      }
      case 'headerBiddingAdUnitMinimumPriceFloor': {
        if (
          updatedCampaign.asset &&
          updatedCampaign.asset.headerBiddingSource
        ) {
          updatedCampaign.asset.headerBiddingSource.prebidMinimumPriceFloor =
            priceFloor
          updatedCampaign.asset.headerBiddingSource.prebidMinimumPriceFloorCurrency =
            priceFloorCurrency
        }
        break
      }
      case 'thirdPartySellerDomain': {
        updatedCampaign.thirdPartyReferenceChainId = thirdPartyReferenceChainId
        break
      }
    }
    return updatedCampaign
  })
}

function sanitizeCampaigns(campaigns: Campaign[]): Campaign[] {
  return campaigns.map((campaign) => {
    campaign.schedule = removeNullFields(campaign.schedule)
    campaign.endDate = campaign.endDate ? campaign.endDate : null
    return campaign
  })
}

/*
 * This dummy dropdown prevents the main one from selecting the first value by default,
 * which happenas a result of a known issue with Fabric fixed in a release posterior to
 * our current dependency. https://github.com/microsoft/fluentui/pull/10755
 */
function PreventInputFieldDefaultSelection() {
  return (
    <Dropdown
      options={[{ key: 'foo', text: 'foo ' }]}
      style={{
        visibility: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    ></Dropdown>
  )
}

function isHbCampaign(campaign: Campaign): boolean {
  return !!campaign.asset && !!campaign.asset.headerBiddingSource
}
